<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells><h1 class="title">Balance Report ({{ balance_data.property.name }})</h1></app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Created by:</span><span>{{ balance_data.created_by.full_name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>From:</span><span>{{ formatDateTime(balance_data.start_time) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>To:</span><span>{{ formatDateTime(balance_data.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>
      <app-cells><h2 class="title">Accounts</h2></app-cells>
      <v-client-table
        :data="table_accounts.items"
        :columns="table_accounts.columns"
        :options="table_accounts.options"
        class="table-default"
      >
        <template #account="{ row }">{{ row.account.name }}</template>
        <template #reported="{ row }">{{ amountFormat(row.balance) }} {{ row.account.currency.symbol }}</template>
        <template #actual="{ row }">{{ amountFormat(row.balance_actual) }} {{ row.account.currency.symbol }}</template>
        <template #difference="{ row }">{{ amountFormat(row.balance_difference) }} {{ row.account.currency.symbol }}</template>
      </v-client-table>
      <template v-if="table_cash.items.length">
        <app-cells><h2 class="title">Cloudbeds Cash</h2></app-cells>
        <v-client-table
          :data="table_cash.items"
          :columns="table_cash.columns"
          :options="table_cash.options"
          class="table-default"
          :class="{ 'table-default--loading': table_cash_preloader }"
        >
          <template #h__user><div class="table-default__left">User</div></template>
          <template #h__guest><div class="table-default__left">Guest</div></template>
          <template #user="{ row }"><div class="table-default__left">{{ row.username }}</div></template>
          <template #date="{ row }">{{ formatDateTime(row.transaction_date_time) }}</template>
          <template #guest="{ row }">
            <router-link
              v-if="row.reservation?.id"
              :to="{ name: 'hotel-management-supervising-detail', params: { id: row.reservation.id } }"
              class="table-default__link-border"
            >
              {{ row.guest_id_name }}
            </router-link>
            <router-link
              v-if="row.house_account"
              :to="{ name: 'hotel-management-supervising-house-accounts-detail', params: { id: row.house_account } }"
              class="table-default__link-border"
            >
              {{ row.guest_id_name }}
            </router-link>
          </template>
          <template #room="{ row }">{{ row.sub_reservation?.room.room_name || '-' }}</template>
          <template #amount="{ row }">{{ amountFormat(row.amount) }} $</template>
          <template #cash="{ row }">
            <span v-if="!row.cashflow" class="status-tag" style="background-color: #e85c20">Missing</span>
            <span v-else class="status-tag" style="background-color: #77b017">{{ amountFormat(row.cashflow.amount) }} {{ row.cashflow.account.currency.symbol }}</span>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationCash"
              v-if="pagination_cash.pages > 1"
              v-model="pagination_cash.page"
              :page-count="pagination_cash.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_cash.count" :count="table_cash.items.length" :page="pagination_cash.page" />
        </app-cells>
      </template>
      <template v-if="table_pos.items.length">
        <app-cells><h2 class="title">Cloudbeds POS</h2></app-cells>
        <v-client-table
          :data="table_pos.items"
          :columns="table_pos.columns"
          :options="table_pos.options"
          class="table-default"
          :class="{ 'table-default--loading': table_pos_preloader }"
        >
          <template #h__user><div class="table-default__left">User</div></template>
          <template #h__guest><div class="table-default__left">Guest</div></template>
          <template #user="{ row }"><div class="table-default__left">{{ row.username }}</div></template>
          <template #date="{ row }">{{ formatDateTime(row.transaction_date_time) }}</template>
          <template #guest="{ row }">
            <router-link
              v-if="row.reservation?.id"
              :to="{ name: 'hotel-management-supervising-detail', params: { id: row.reservation.id } }"
              class="table-default__link-border"
            >
              {{ row.guest_id_name }}
            </router-link>
            <router-link
              v-if="row.house_account"
              :to="{ name: 'hotel-management-supervising-house-accounts-detail', params: { id: row.house_account } }"
              class="table-default__link-border"
            >
              {{ row.guest_id_name }}
            </router-link>
          </template>
          <template #room="{ row }">{{ row.sub_reservation?.room.room_name || '-' }}</template>
          <template #amount="{ row }">{{ amountFormat(row.amount) }} $</template>
          <template #pos="{ row }">
            <span v-if="!row.pos_transaction" class="status-tag" style="background-color: #e85c20">Missing</span>
            <span v-else class="status-tag" style="background-color: #77b017">{{ amountFormat(row.pos_transaction.amount) }} $ ({{ row.pos_transaction.rrn }})</span>
          </template>
        </v-client-table>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationPos"
              v-if="pagination_pos.pages > 1"
              v-model="pagination_pos.page"
              :page-count="pagination_pos.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_pos.count" :count="table_pos.items.length" :page="pagination_pos.page" />
        </app-cells>
      </template>
      <template v-if="table_cashflow_data.length">
        <app-cells><h2 class="title">Cashflow</h2></app-cells>
        <div class="transaction__table" :class="{ 'transaction__table--loading': table_preloader_cashflow }">
          <div class="transaction__table-row transaction__table-row--head">
            <div class="transaction__table-col">Date</div>
            <div class="transaction__table-col">Debit</div>
            <div class="transaction__table-col">Credit</div>
            <div class="transaction__table-col">Item / Description</div>
            <div class="transaction__table-col">Account</div>
            <div class="transaction__table-col">Balance</div>
            <div class="transaction__table-col"></div>
          </div>
          <div v-for="transaction of table_cashflow_data" :key="transaction.id" class="transaction__table-row">
            <div class="transaction__table-col">
              <b v-tooltip="{content: tooltipContent(transaction), placement: 'right', offset: 10, html: true}">
                {{ dateFormat(transaction.date) }}
              </b>
            </div>
            <div class="transaction__table-col">
              <template v-if="transaction.is_transfer">
                <b v-if="transaction.transfer_transaction_to" class="transaction__table-amount transaction__table-amount--green">
                  {{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.transfer_transaction_to.amount) }} {{ transaction.transfer_transaction_to.account.currency.code }}
                </b>
              </template>
              <template v-else>
                <b v-if="transaction.type === 1" class="transaction__table-amount transaction__table-amount--green">
                  {{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.amount) }} {{ transaction.account.currency.code }}
                </b>
              </template>
            </div>
            <div class="transaction__table-col">
              <template v-if="transaction.is_transfer">
                <b class="transaction__table-amount transaction__table-amount--red">
                  -{{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.amount) }} {{ transaction.account.currency.code }}
                </b>
              </template>
              <template v-else>
                <b v-if="transaction.type === 2" class="transaction__table-amount transaction__table-amount--red">
                  -{{ new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(transaction.amount) }} {{ transaction.account.currency.code }}
                </b>
              </template>
            </div>
            <div class="transaction__table-col transaction__table-col--flex-col">
              <b v-if="transaction.is_transfer">Transfer funds</b>
              <template v-else>
                <button
                  v-if="transaction.items.length > 1"
                  v-tooltip="{
                  content: tooltipContentCashflowItems(transaction),
                  placement: 'right',
                  offset: 10,
                  html: true,
                  trigger: 'click',
                  classes: 'tooltip-cashflow'
                }"
                  class="transaction__table-btn"
                >
                  {{ transaction.items.length }} items
                  <span v-if="transaction.responsible">{{ transaction.responsible.full_name }}</span>
                </button>
                <template v-else>
                  <b v-if="transaction.type === 1">{{ transaction.items[0]?.item.name_revenue }} <span v-if="transaction.responsible" class="transaction__table-description">{{ transaction.responsible.full_name }}</span></b>
                  <b v-if="transaction.type === 2">{{ transaction.items[0]?.item.name_expense }} <span v-if="transaction.responsible" class="transaction__table-description">{{ transaction.responsible.full_name }}</span></b>
                </template>
              </template>
              <span v-if="transaction.description" class="transaction__table-description">{{ transaction.description }}</span>
            </div>
            <div class="transaction__table-col transaction__table-col--flex-col">
              <template v-if="transaction.is_transfer">
                <template v-if="transaction.transfer_transaction_to">
                  <b>{{ transaction.transfer_transaction_to.account.name }}</b>
                  <b v-if="transaction.transfer_transaction_to.reference" class="transaction__table-description">{{ transaction.transfer_transaction_to.reference }}</b>
                </template>
                <b>{{ transaction.account.name }}</b>
                <b v-if="transaction.reference" class="transaction__table-description">{{ transaction.reference }}</b>
              </template>
              <template v-else>
                <b>{{ transaction.account.name }}</b>
                <b v-if="transaction.reference" class="transaction__table-description">{{ transaction.reference }}</b>
              </template>
            </div>
            <div class="transaction__table-col">
              <template v-if="transaction.is_transfer">
              <span
                v-if="transaction.transfer_transaction_to && transaction.transfer_transaction_to.account_balance !== null"
                class="transaction__table-balance transaction__table-balance--plus"
              >
                {{ new Intl.NumberFormat('en-US').format(transaction.transfer_transaction_to.account_balance) }} {{ transaction.transfer_transaction_to.account.currency.code }}
              </span>
                <template v-else></template>
                <br>
                <span
                  v-if="transaction.account_balance !== null"
                  class="transaction__table-balance transaction__table-balance--minus"
                >
                {{ new Intl.NumberFormat('en-US').format(transaction.account_balance) }} {{ transaction.account.currency.code }}
              </span>
                <template v-else></template>
              </template>
              <span
                v-else
                class="transaction__table-balance"
                :class="{'transaction__table-balance--plus': transaction.type === 1, 'transaction__table-balance--minus': transaction.type === 2, }"
              >
              <template v-if="transaction.account_balance !== null">
                {{ new Intl.NumberFormat('en-US').format(transaction.account_balance) }} {{ transaction.account.currency.code }}
              </template>
              <template v-else></template>
            </span>
            </div>
          </div>
        </div>
        <app-cells position="between">
          <div>
            <paginate
              :clickHandler="onClickPaginationCashflow"
              v-if="pagination_cashflow.pages > 1"
              v-model="pagination_cashflow.page"
              :page-count="pagination_cashflow.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
            />
          </div>
          <app-table-records :all="pagination_cashflow.count" :count="table_cashflow_data.length" :page="pagination_cashflow.page" :limit="40" />
        </app-cells>
      </template>
    </template>
  </div>
</template>

<script>
import {
  getHotelManagementBalanceHistoryDetail,
  getHotelManagementTransactionList,
  getFinanceCashflow
} from '@/http'
import { format, parse, parseISO } from 'date-fns'

export default {
  name: 'HotelManagementBalanceHistoryDetail',
  data() {
    return {
      page_loaded: false,
      balance_data: {},
      table_accounts: {
        items: [],
        columns: ['account', 'reported', 'actual', 'difference'],
        options: {
          headings: {
            account: 'Account',
            reported: 'Reported',
            actual: 'Actual',
            difference: 'Difference',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 100,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      table_cash: {
        items: [],
        columns: [
          'user',
          'date',
          'guest',
          'room',
          'amount',
          'cash',
        ],
        options: {
          headings: {
            'user': 'User',
            'date': 'Date',
            'guest': 'Guest',
            'room': 'Room',
            'amount': 'Amount',
            'cash': 'Cashflow',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_cash: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_cash_preloader: false,
      table_pos: {
        items: [],
        columns: [
          'efd',
          'user',
          'date',
          'guest',
          'room',
          'amount',
          'pos',
        ],
        options: {
          headings: {
            'efd': 'EFD',
            'user': 'User',
            'date': 'Date',
            'guest': 'Guest',
            'room': 'Room',
            'amount': 'Amount',
            'pos': 'POS Slip',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination_pos: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_pos_preloader: false,
      table_cashflow_data: [],
      pagination_cashflow: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader_cashflow: false,
    }
  },
  created() {
    Promise.all([
      this.fetchData(),
      this.fetchItemsCash(),
      this.fetchItemsPos(),
      this.fetchItemsCashFlow()
    ])
      .then(() => {
        this.page_loaded = true
      })
  },
  methods: {
    async fetchData() {
      const response = await getHotelManagementBalanceHistoryDetail(this.$route.params.id)
      this.balance_data = response.data
      this.table_accounts.items = response.data.accounts
    },
    async fetchItemsCash() {
      this.table_cash_preloader = true
      const response = await getHotelManagementTransactionList({
        page: this.pagination_cash.page,
        page_size: 20,
        balance_history: this.$route.params.id,
        cash_category: 'all'
      })
      this.table_cash.items = response.data.results
      this.pagination_cash.pages = response.data.pages
      this.pagination_cash.count = response.data.count
      this.table_cash_preloader = false
    },
    async fetchItemsPos() {
      this.table_pos_preloader = true
      const response = await getHotelManagementTransactionList({
        page: this.pagination_pos.page,
        page_size: 20,
        balance_history: this.$route.params.id,
        pos_category: 'all'
      })
      this.table_pos.items = response.data.results
      this.pagination_pos.pages = response.data.pages
      this.pagination_pos.count = response.data.count
      this.table_pos_preloader = false
    },
    async fetchItemsCashFlow() {
      this.table_preloader_cashflow = true
      const response = await getFinanceCashflow({
        page: this.pagination_cashflow.page,
        page_size: 40,
        balance_history: this.$route.params.id,
      })
      this.table_cashflow_data = response.data.results
      this.pagination_cashflow.pages = response.data.pages
      this.pagination_cashflow.count = response.data.count
      this.table_preloader_cashflow = false
    },
    onClickPaginationCash(page) {
      this.pagination_cash.page = page
      this.fetchItemsCash()
    },
    onClickPaginationPos(page) {
      this.pagination_pos.page = page
      this.fetchItemsPos()
    },
    onClickPaginationCashflow(page) {
      this.pagination_cashflow.page = page
      this.fetchItemsCashFlow()
    },
    formatDateTime(date) {
      return date ? format(parseISO(date), 'dd MMMM yyyy HH:mm') : '-'
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy')
    },
    amountFormat(amount) {
      return amount ? new Intl.NumberFormat('ru-RU').format(amount) : '-'
    },
    tooltipContent(transaction) {
      return `${transaction.created_by?.full_name || 'System' }<br>${this.formatDateTime(transaction.created_at)}`
    },
    tooltipContentCashflowItems(transaction) {
      let content = ''
      transaction.items.forEach(({item, amount, supplier}) => {
        content += `<span><b>Item:</b> ${transaction.type === 1 ? item.name_revenue : item.name_expense} <b>Amount:</b> ${new Intl.NumberFormat('en-US', {minimumFractionDigits: 2}).format(amount)} ${transaction.account.currency.code} ${supplier ? `<b>Supplier</b>: ${supplier.legal_name}` : ''}</span>`
      })
      return content
    },
  }
}
</script>

<style scoped lang="sass">
.transaction__table
  background-color: #fff

  &:before
    content: ''
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    background-color: rgba(#fff, 0.8)
    opacity: 0
    z-index: 2
    pointer-events: none
    transition: opacity 0.3s

.transaction__table--loading:before
  opacity: 1
  pointer-events: auto

.transaction__table-row
  display: grid
  align-items: center
  grid-template-columns: 120px 180px 180px 1fr 250px 185px 80px
  border: 1px solid #eaeaea
  border-top: none

  &:first-child
    border-color: #f6f6f6
    background-color: #f6f6f6
    border-radius: 5px 5px 0 0

  &:nth-child(odd)
    background-color: #f6f6f6

.transaction__table-row--head .transaction__table-col
  font-weight: 500

.transaction__table-col
  padding: 10px

  b
    font-weight: 500

.transaction__table-btn
  font-weight: 500
  text-align: left
  transition: color 0.3s

  &:hover
    color: $color-theme

  span
    color: #626262
    font-size: 13px

.transaction__table-col--flex-col
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 3px

.transaction__table-col--flex
  display: flex
  gap: 10px
  align-items: center
  justify-content: center

  button
    line-height: 0
    transition: transform 0.2s

    &:hover
      transform: translateY(-1px)

    img
      width: 25px
      height: 25px

.transaction__table-amount--green
  color: $color-theme

.transaction__table-amount--red
  color: #e85c20

.transaction__table-description
  color: #626262
  font-size: 13px

.transaction__table-balance
  position: relative
  padding-left: 20px

  &:before
    content: ''
    position: absolute
    left: 8px
    top: 5px
    width: 8px
    height: 5px

.transaction__table-balance--minus:before
  background-image: url("~@/assets/img/balance-minus.svg")

.transaction__table-balance--plus:before
  background-image: url("~@/assets/img/balance-plus.svg")
</style>

